import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, MapPin, Calendar, Moon, Camera, X, ChevronLeft, ChevronRight, CameraIcon } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import ShareMenu from "./ShareMenu";
import AddTripPhotoModal from "./AddTripPhotoModal";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const Stars = ({ rating }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? "fill-[#ff4500] text-[#ff4500]" : "text-[#ddd]"}`} strokeWidth={1.5} />
    ))}
  </span>
);

export default function TripStoryCard({ story, onChange }) {
  const { user } = useAuth();
  const [photoIdx, setPhotoIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null); // null = closed, number = open at index
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const photos = story.photos || [];
  const cover = photos[photoIdx]?.image_data_url;
  const hasMine = user && story.kudos?.includes(user.user_id);
  const kudosCount = (story.kudos || []).length;
  const highlight = story.highlight || "";
  const isOwner = user && story.user_id === user.user_id;
  const canAddPhoto = isOwner && photos.length < 4;
  // Long stories get a "Read more" toggle. ~280 chars ≈ 4 lines of body copy
  // on a mobile card width — matches the existing line-clamp-4 visual cut.
  const needsToggle = highlight.length > 280;

  // Lightbox keyboard nav (Esc to close, arrows to navigate)
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i > 0 ? i - 1 : photos.length - 1));
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i < photos.length - 1 ? i + 1 : 0));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, photos.length]);

  const toggleKudos = async () => {
    if (!user) return toast.info("Log in to give kudos");
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wall/${story.story_id}/kudos`);
      onChange?.({ ...story, kudos: data.given
        ? [...(story.kudos || []), user.user_id]
        : (story.kudos || []).filter((u) => u !== user.user_id) });
    } catch (e) {
      toast.error("Couldn't update kudos");
    } finally { setBusy(false); }
  };

  return (
    <article
      className="bg-white border hairline overflow-hidden flex flex-col break-inside-avoid mb-6 shadow-[0_2px_0_0_#111] hover:shadow-[0_4px_0_0_#111] hover:-translate-y-0.5 transition-all"
      data-testid={`trip-story-${story.story_id}`}
    >
      {cover ? (
        <div className="relative bg-[#111] aspect-[4/3]">
          <button
            type="button"
            onClick={() => setLightboxIdx(photoIdx)}
            className="block w-full h-full cursor-zoom-in"
            aria-label="View photo full size"
            data-testid={`trip-story-${story.story_id}-zoom`}
          >
            <img
              src={cover}
              alt={`${story.campsite_name} — ${story.user_name}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
          {photos.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {photos.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setPhotoIdx(i); }}
                  className={`w-2 h-2 rounded-full ${i === photoIdx ? "bg-white" : "bg-white/40"}`}
                  aria-label={`Photo ${i + 1}`}
                  data-testid={`trip-story-${story.story_id}-dot-${i}`}
                />
              ))}
            </div>
          )}
          {story.use_for_campsite && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider bg-black/70 text-white px-2 py-1">
              <Camera className="w-3 h-3" strokeWidth={2} /> Shared with {story.campsite_name.split(" ").slice(0, 2).join(" ")}
            </span>
          )}
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gradient-to-br from-[#fff4ee] to-[#ffe2d0] flex flex-col items-center justify-center gap-3">
          <Camera className="w-10 h-10 text-[#ff4500]/40" strokeWidth={1.5} />
          {canAddPhoto && (
            <button
              type="button"
              onClick={() => setShowAddPhoto(true)}
              className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-3 py-1.5 border-2 border-[#ff4500] text-[#ff4500] bg-white hover:bg-[#ff4500] hover:text-white transition-colors"
              data-testid={`trip-story-${story.story_id}-add-first-photo`}
            >
              <Camera className="w-3 h-3" strokeWidth={2} /> Add a photo now
            </button>
          )}
        </div>
      )}

      <div className="p-4 flex-1 flex flex-col gap-2">
        <Link
          to={`/campsites/${story.campsite_id}`}
          className="font-display text-lg leading-tight hover:text-[#ff4500] transition-colors"
          data-testid={`trip-story-${story.story_id}-site`}
        >
          {story.campsite_name}
        </Link>
        <div className="text-[11px] mono text-[#666] flex items-center gap-1">
          <MapPin className="w-3 h-3" strokeWidth={1.75} />
          {story.campsite_region || "United Kingdom"}
        </div>

        <p
          className={`text-sm leading-snug text-[#222] mt-1 whitespace-pre-line ${expanded || !needsToggle ? "" : "line-clamp-4"}`}
          data-testid={`trip-story-${story.story_id}-text`}
        >
          {highlight}
        </p>
        {needsToggle && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="self-start text-[11px] mono uppercase tracking-wider text-[#ff4500] hover:underline mt-1"
            data-testid={`trip-story-${story.story_id}-toggle`}
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}

        <div className="mt-2 flex items-center gap-3 text-[11px] mono text-[#555]">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" strokeWidth={1.75} /> {fmtDate(story.date_visited)}</span>
          <span className="inline-flex items-center gap-1"><Moon className="w-3 h-3" strokeWidth={1.75} /> {story.nights_stayed}{story.nights_stayed === 1 ? " night" : " nights"}</span>
          <Stars rating={story.rating} />
        </div>

        <div className="mt-3 pt-3 border-t hairline flex items-center justify-between gap-2">
          <span className="text-[11px] mono text-[#888] truncate">— {story.user_name}</span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {canAddPhoto && photos.length > 0 && (
              <button
                onClick={() => setShowAddPhoto(true)}
                className="inline-flex items-center justify-center w-8 h-8 border hairline hover:bg-[#fff4ee]"
                aria-label="Add another photo to this trip"
                title="Add another photo"
                data-testid={`trip-story-${story.story_id}-add-photo`}
              >
                <CameraIcon className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={1.75} />
              </button>
            )}
            <ShareMenu
              url={`${process.env.REACT_APP_BACKEND_URL}/api/share/wall/${story.story_id}`}
              title={`${story.campsite_name} · CampCompass UK`}
              text={story.highlight ? `"${story.highlight.slice(0, 140)}" — UK camping recommendation`
                                    : `Loved this UK campsite — ${story.campsite_name}`}
              imageDataUrl={cover}
              buttonClassName="inline-flex items-center justify-center w-8 h-8 border hairline hover:bg-[#fff4ee]"
              iconOnly
              testId={`trip-story-${story.story_id}-share`}
            />
            <button
              onClick={toggleKudos}
              disabled={busy}
              className={`inline-flex items-center gap-1 text-[12px] mono px-2 py-1 border hairline transition-colors ${hasMine ? "bg-[#ff4500] text-white border-[#ff4500]" : "hover:bg-[#fff4ee]"}`}
              data-testid={`trip-story-${story.story_id}-kudos`}
              aria-label={hasMine ? "Remove kudos" : "Give kudos"}
            >
              <Heart className={`w-3.5 h-3.5 ${hasMine ? "fill-white" : ""}`} strokeWidth={1.75} />
              {kudosCount}
            </button>
          </div>
        </div>
      </div>

      {lightboxIdx !== null && photos[lightboxIdx]?.image_data_url && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          data-testid={`trip-story-${story.story_id}-lightbox`}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
            aria-label="Close"
            data-testid={`trip-story-${story.story_id}-lightbox-close`}
          >
            <X className="w-5 h-5" />
          </button>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i > 0 ? i - 1 : photos.length - 1)); }}
                className="absolute left-2 sm:left-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
                aria-label="Previous photo"
                data-testid={`trip-story-${story.story_id}-lightbox-prev`}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i < photos.length - 1 ? i + 1 : 0)); }}
                className="absolute right-2 sm:right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
                aria-label="Next photo"
                data-testid={`trip-story-${story.story_id}-lightbox-next`}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={photos[lightboxIdx].image_data_url}
            alt={`${story.campsite_name} — photo ${lightboxIdx + 1} of ${photos.length}`}
            className="max-w-[95vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {photos.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-xs mono">
              {lightboxIdx + 1} / {photos.length}
            </div>
          )}
        </div>
      )}

      {showAddPhoto && (
        <AddTripPhotoModal
          story={story}
          onClose={() => setShowAddPhoto(false)}
          onUpdated={(fresh) => onChange?.(fresh)}
        />
      )}
    </article>
  );
}